/**
 * ReceivingDetail — Review and approve a supplier invoice · v1.2.
 *
 * Steps: (1) header meta from parser · (2) extracted product grid ·
 * (3) totals · (4) receipt reconciliation lifecycle · (5) approve → FIFO + tank
 * inventory increment.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, CheckCircle, Warning, Info, Truck, Storefront,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { fmtCalendarDate, fmtCalendarDateShort } from "@/lib/calendarDate";
import ConfidencePanel from "@/components/receiving/ConfidencePanel";
import ReceiptStatePipeline from "@/components/receiving/ReceiptStatePipeline";
import DuplicateComparisonPanel from "@/components/receiving/DuplicateComparisonPanel";

const PRODUCT_LABEL = {
  regular: "Regular Gasoline",
  premium: "Premium Gasoline",
  clear_diesel: "Clear Diesel",
  dyed_diesel: "Dyed Diesel",
  furnace_oil: "Furnace Oil",
  def: "DEF",
};

const fmtMoney = (v, digits = 2) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });
};

export default function ReceivingDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(false);
  const [notes, setNotes] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/receiving/${id}`);
      setDoc(data);
      setNotes(data.notes || "");
    } catch (e) {
      toast.error("Could not load invoice.");
    } finally {
      setLoading(false);
    }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const approve = async () => {
    setApproving(true);
    try {
      const { data } = await api.post(`/receiving/${id}/approve`, { notes });
      toast.success(
        `Invoice approved · ${data.fifo_receipts.length} FIFO layer${data.fifo_receipts.length === 1 ? "" : "s"} created, tank inventory updated.`
      );
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const kind = typeof detail === "object" ? detail?.kind : null;
      if (kind === "already_approved") {
        toast.info(`Already approved by ${detail.approved_by}.`);
        await load();
      } else if (kind === "parser_issues") {
        toast.error("Parser errors must be resolved before approving.");
      } else {
        const msg = typeof detail === "string" ? detail : detail?.message || "Approval failed";
        toast.error(msg);
      }
    } finally {
      setApproving(false);
    }
  };

  if (loading) return <div className="p-6 text-sm text-slate-500" data-testid="recv-detail-loading">Loading…</div>;
  if (!doc) return <div className="p-6 text-sm text-slate-500">Not found.</div>;

  const p = doc.parsed || {};
  const match = doc.three_way_match || {};
  const approved = doc.status === "approved";
  const criticalIssues = (p.issues || []).filter((i) => i.severity === "error");

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="receiving-detail">
      <button
        onClick={() => nav("/receiving")}
        className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900"
        data-testid="recv-detail-back"
      >
        <ArrowLeft size={14} /> Receiving
      </button>

      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs text-slate-500 uppercase tracking-wide">Supplier Invoice</div>
          <h1 className="text-2xl font-semibold text-slate-900">
            {doc.supplier} · #{p.invoice_number || "—"}
          </h1>
          <p className="text-sm text-slate-600 mt-0.5">
            Delivery {fmtCalendarDateShort(p.delivery_date)} {p.delivery_time || ""} · {p.terminal || "—"}
          </p>
        </div>
        {approved ? (
          <span data-testid="recv-detail-status-approved"
                className="inline-flex items-center gap-1.5 rounded-full border border-emerald-300 bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-800">
            <CheckCircle size={14} weight="fill" /> Approved
          </span>
        ) : (
          <span data-testid="recv-detail-status-parsed"
                className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-800">
            Pending Review
          </span>
        )}
      </header>

      {/* Sprint 9.3 · ARC-015 Receipt Reconciliation Lifecycle */}
      <ReceiptStatePipeline
        invoiceId={id}
        confidence={doc.confidence}
        onTransitioned={load}
      />

      {/* Sprint 10 · Duplicate invoice comparison (only when flagged) */}
      <DuplicateComparisonPanel
        invoiceId={id}
        duplicateOf={doc.confidence?.duplicate_of}
      />

      {/* Sprint 9.2 · AI Confidence Enrichment */}
      <ConfidencePanel
        invoiceId={id}
        confidence={doc.confidence}
        onRefreshed={load}
      />

      {/* Step 1 — Extracted header fields */}
      <section className="rounded-lg border border-slate-200 bg-white p-5" data-testid="recv-header-fields">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-3">Invoice details · AI extracted</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3 text-sm">
          <MetaCell label="Invoice Number"    value={p.invoice_number} />
          <MetaCell label="Billing Date"      value={fmtCalendarDate(p.billing_date)} />
          <MetaCell label="Supplier"          value={doc.supplier} />
          <MetaCell label="Terminal"          value={p.terminal} />
          <MetaCell label="Truck"             value={p.truck_number} />
          <MetaCell label="Carrier ID"        value={p.carrier_id} />
          <MetaCell label="Driver ID"         value={p.driver_id} />
          <MetaCell label="Delivery"          value={p.delivery_date && `${fmtCalendarDate(p.delivery_date)}${p.delivery_time ? " · " + p.delivery_time : ""}`} />
          <MetaCell label="Contract"          value={p.contract_number} />
          <MetaCell label="Purchase Account"  value={p.purchase_account} />
          <MetaCell label="Payment Terms"     value={p.payment_terms} />
          <MetaCell label="Due Date"          value={fmtCalendarDate(p.due_date)} />
        </div>
      </section>

      {/* Step 2 — Product review grid */}
      <section className="rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid="recv-product-grid">
        <div className="border-b border-slate-200 px-5 py-3">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-600">Products delivered · AI extracted</div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-600 border-b border-slate-200">
            <tr>
              <th className="px-5 py-2">Product</th>
              <th className="px-3 py-2 text-right">Litres</th>
              <th className="px-3 py-2 text-right">Invoice Unit Price</th>
              <th className="px-3 py-2 text-right">Extended</th>
            </tr>
          </thead>
          <tbody>
            {(p.products || []).map((row, i) => (
              <tr key={i} className="border-b border-slate-100 last:border-b-0" data-testid={`recv-product-row-${i}`}>
                <td className="px-5 py-2.5">
                  <div className="font-semibold text-slate-900">
                    {PRODUCT_LABEL[row.internal_product_code] || row.supplier_description || "Unknown"}
                  </div>
                  <div className="text-[11px] text-slate-500 font-mono">{row.supplier_code} · {row.supplier_description}</div>
                </td>
                <td className="px-3 py-2.5 text-right font-mono tabular-nums text-slate-900">{fmtMoney(row.litres, 3)}</td>
                <td className="px-3 py-2.5 text-right font-mono tabular-nums text-slate-900">${fmtMoney(row.unit_price, 4)}</td>
                <td className="px-3 py-2.5 text-right font-mono tabular-nums text-slate-900">${fmtMoney(row.extended_amount, 2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {/* Step 3 — Totals */}
      <section className="rounded-lg border border-slate-200 bg-white p-5" data-testid="recv-totals">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-3">Totals · AI extracted</div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-x-6 gap-y-3 text-sm">
          <MoneyCell label="Subtotal"        value={p.subtotal} />
          <MoneyCell label="Provincial Fuel Tax" value={p.provincial_fuel_tax} />
          <MoneyCell label="GST/HST"         value={p.gst_hst} />
          <MoneyCell label="Total"           value={p.total} strong />
          <MoneyCell label="Amount Due"      value={p.amount_due} strong tone="emerald" />
        </div>
      </section>

      {/* Step 4 — Receipt Reconciliation lifecycle (ARC-011) */}
      <ReceiptReconciliationBlock doc={doc} match={match} onFreightAttached={load} />

      {/* Parser issues */}
      {(p.issues || []).length > 0 && (
        <section className="rounded-lg border border-amber-200 bg-amber-50 p-4" data-testid="recv-parser-issues">
          <div className="flex items-center gap-2 text-amber-900 text-sm font-semibold mb-1">
            <Warning size={16} weight="fill" /> Parser notes
          </div>
          <ul className="text-xs text-amber-900 list-disc list-inside space-y-0.5">
            {(p.issues || []).map((i, k) => (
              <li key={k}>[{i.severity}] {i.field}: {i.message}</li>
            ))}
          </ul>
        </section>
      )}

      {/* Step 5 — Notes + Approve */}
      {!approved ? (
        <section className="rounded-lg border border-slate-300 bg-white p-5" data-testid="recv-approve-panel">
          <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">Notes (optional)</label>
          <textarea
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Anything worth flagging on this receipt?"
            className="w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none"
            data-testid="recv-approve-notes"
          />
          <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
            <div className="text-xs text-slate-500">
              Approving creates a FIFO cost layer per product and increments today's tank inventory with the delivered litres.
            </div>
            <button
              onClick={approve}
              disabled={approving || criticalIssues.length > 0}
              data-testid="recv-approve-btn"
              className="rounded-md bg-emerald-700 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-800 disabled:opacity-50 disabled:cursor-not-allowed"
              title={criticalIssues.length > 0 ? "Resolve parser errors first" : "Approve receipt · create FIFO layer · update inventory"}
            >
              <CheckCircle size={14} weight="bold" className="inline-block mr-1.5 -mt-0.5" />
              {approving ? "Approving…" : "Approve Receipt"}
            </button>
          </div>
        </section>
      ) : (
        <section className="rounded-lg border border-emerald-200 bg-emerald-50 p-5" data-testid="recv-approved-audit">
          <div className="flex items-center gap-2 text-emerald-900 font-semibold">
            <CheckCircle size={16} weight="fill" /> Receipt approved by {doc.approved_by}
          </div>
          <p className="text-xs text-emerald-800 mt-1">
            {new Date(doc.approved_at).toLocaleString()} · {(doc.fifo_receipts || []).length} FIFO layer{(doc.fifo_receipts||[]).length===1?"":"s"} created · Inventory updated
          </p>
          {doc.notes && <p className="text-xs text-emerald-900 italic mt-1">"{doc.notes}"</p>}
          {(doc.fifo_receipts || []).length > 0 && (
            <ul className="mt-3 space-y-0.5 text-xs text-emerald-900">
              {doc.fifo_receipts.map((r) => (
                <li key={r.id} data-testid={`recv-fifo-${r.id}`} className="flex items-center gap-2">
                  <Truck size={12} /> {PRODUCT_LABEL[r.product_code] || r.product_code}{" — "}
                  <span className="font-mono">{fmtMoney(r.litres, 0)} L @ ${fmtMoney(r.landed_unit_cost, 4)}/L</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </div>
  );
}

function MetaCell({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-slate-900 truncate" title={value || ""}>{value || "—"}</div>
    </div>
  );
}

function MoneyCell({ label, value, strong, tone }) {
  const cls = strong
    ? tone === "emerald"
      ? "text-emerald-900 font-semibold"
      : "text-slate-900 font-semibold"
    : "text-slate-800";
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`font-mono tabular-nums ${cls}`}>{value ? `$${fmtMoney(value, 2)}` : "—"}</div>
    </div>
  );
}

/**
 * ReceiptReconciliationBlock — ARC-011 lifecycle display.
 *
 * Replaces the old red variance panel. Shows the multi-invoice
 * reconciliation lifecycle (Fuel Invoice ✓ → Freight Invoice ⏳ →
 * Final Landed Cost) and lets the operator attach a freight cost when
 * the carrier's invoice arrives.
 */
function ReceiptReconciliationBlock({ doc, match, onFreightAttached }) {
  const receiptStatus = doc.receipt_status || (doc.status === "approved" ? "WAITING_FOR_FREIGHT" : "DRAFT");
  const fuelMatched = doc.status === "approved";
  const freightMatched = receiptStatus === "COMPLETE";
  const [freightOpen, setFreightOpen] = useState(false);

  // Estimated (provisional) landed cost = supplier unit price only.
  // Final landed cost = supplier + freight, shown once attached.
  const estimated = (doc.fifo_receipts || []).map((r) => ({
    product: r.product_code, per_litre: r.landed_unit_cost,
  }));

  return (
    <section
      className="rounded-lg border border-slate-200 bg-white overflow-hidden"
      data-testid="recv-receipt-reconciliation"
    >
      <div className="border-b border-slate-200 px-5 py-3 flex items-center justify-between">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-600">Receipt Reconciliation</div>
          <div className="text-sm text-slate-600 mt-0.5">
            Lifecycle · {receiptStatus.replace(/_/g, " ").toLowerCase()}
          </div>
        </div>
        <span
          data-testid="recv-lifecycle-status"
          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white ${
            freightMatched ? "bg-emerald-600" : fuelMatched ? "bg-amber-600" : "bg-slate-500"
          }`}
        >
          {freightMatched ? "Complete" : fuelMatched ? "Waiting for Freight" : "Awaiting Fuel Invoice"}
        </span>
      </div>

      {/* Three-stage lifecycle */}
      <div className="px-5 py-4 grid grid-cols-1 md:grid-cols-3 gap-4">
        <LifecycleStep
          testid="lifecycle-fuel"
          label="Fuel Invoice"
          status={fuelMatched ? "done" : "pending"}
          detail={fuelMatched
            ? `Matched ${match.price_notification_found ? "· PN " + fmtCalendarDateShort(match.price_notification_effective_date) : ""}`
            : "Waiting for approval"}
        />
        <LifecycleStep
          testid="lifecycle-freight"
          label="Freight Invoice"
          status={freightMatched ? "done" : fuelMatched ? "waiting" : "future"}
          detail={freightMatched
            ? `${doc.freight?.carrier || "Carrier"} · Invoice ${doc.freight?.freight_invoice_number || "—"}`
            : fuelMatched ? "Typically 5–10 days after delivery" : "—"}
        />
        <LifecycleStep
          testid="lifecycle-landed"
          label={freightMatched ? "Final Landed Cost" : "Estimated Landed Cost"}
          status={freightMatched ? "done" : fuelMatched ? "provisional" : "future"}
          detail={
            <div className="text-xs space-y-0.5">
              {estimated.map((e) => (
                <div key={e.product} className="font-mono">
                  {PRODUCT_LABEL[e.product] || e.product}: ${fmtMoney(e.per_litre, 4)}/L
                  {!freightMatched && <span className="text-slate-500"> (est.)</span>}
                </div>
              ))}
            </div>
          }
        />
      </div>

      {/* Attach freight action */}
      {fuelMatched && !freightMatched && (
        <div className="border-t border-slate-200 px-5 py-3 flex flex-wrap items-center justify-between gap-3">
          <div className="text-xs text-slate-500">
            When the freight invoice arrives, attach it here to finalize the landed cost and mark this receipt COMPLETE.
          </div>
          <button
            data-testid="recv-attach-freight-open"
            onClick={() => setFreightOpen(true)}
            className="rounded-md bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-800"
          >
            Attach Freight Invoice
          </button>
        </div>
      )}

      {freightMatched && doc.freight && (
        <div className="border-t border-emerald-200 bg-emerald-50 px-5 py-3 text-xs text-emerald-900" data-testid="recv-freight-audit">
          <b>Freight attached:</b> {doc.freight.carrier} · Invoice #{doc.freight.freight_invoice_number || "—"} ·
          ${doc.freight.freight_per_litre}/L · attached by {doc.freight.attached_by} on {new Date(doc.freight.attached_at).toLocaleString()}.
          {doc.freight.notes && <> · <i>"{doc.freight.notes}"</i></>}
        </div>
      )}

      {freightOpen && (
        <AttachFreightDialog
          receipt={doc}
          onClose={() => setFreightOpen(false)}
          onSuccess={() => { setFreightOpen(false); onFreightAttached?.(); }}
        />
      )}
    </section>
  );
}

function LifecycleStep({ label, status, detail, testid }) {
  const cls =
    status === "done"        ? "border-emerald-300 bg-emerald-50 text-emerald-900"
  : status === "waiting"     ? "border-amber-300  bg-amber-50  text-amber-900"
  : status === "provisional" ? "border-amber-300  bg-amber-50  text-amber-900"
                             : "border-slate-200  bg-slate-50  text-slate-500";
  const chip =
    status === "done"        ? "✓ Matched"
  : status === "waiting"     ? "⏳ Waiting"
  : status === "provisional" ? "$ Provisional"
                             : "— Not yet";
  return (
    <div data-testid={testid} className={`rounded-md border p-3 ${cls}`}>
      <div className="text-[11px] font-semibold uppercase tracking-wider opacity-80">{label}</div>
      <div className="text-sm font-semibold mt-0.5">{chip}</div>
      {typeof detail === "string" ? (
        <div className="text-xs mt-1 opacity-90">{detail}</div>
      ) : (
        <div className="mt-1">{detail}</div>
      )}
    </div>
  );
}

function AttachFreightDialog({ receipt, onClose, onSuccess }) {
  const [carrier, setCarrier] = useState("");
  const [invoiceNo, setInvoiceNo] = useState("");
  const [amount, setAmount] = useState("");
  const [perLitre, setPerLitre] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const totalLitres = (receipt.fifo_receipts || []).reduce((sum, r) => sum + Number(r.litres || 0), 0);

  const submit = async () => {
    if (!amount && !perLitre) {
      toast.error("Enter either a freight amount OR a per-litre rate.");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/receiving/${receipt.id}/freight`, {
        carrier: carrier || null,
        freight_invoice_number: invoiceNo || null,
        freight_amount: amount || null,
        freight_per_litre: perLitre || null,
        notes: notes || null,
      });
      toast.success("Freight attached. Landed cost finalized.");
      onSuccess?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Could not attach freight.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="recv-attach-freight-dialog"
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={saving ? undefined : onClose}
    >
      <div className="max-w-md w-full rounded-lg bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="border-b border-slate-200 px-5 py-3">
          <h2 className="text-base font-semibold text-slate-900">Attach Freight Invoice</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Total delivered litres on this receipt: <b>{totalLitres.toLocaleString()} L</b>. Provide either the total freight amount or a per-litre rate.
          </p>
        </div>
        <div className="px-5 py-4 space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs text-slate-600">Carrier</span>
              <input value={carrier} onChange={(e) => setCarrier(e.target.value)}
                     data-testid="recv-freight-carrier"
                     className="mt-0.5 w-full rounded border border-slate-300 px-2 py-1 text-sm focus:border-slate-500 focus:outline-none" />
            </label>
            <label className="block">
              <span className="text-xs text-slate-600">Freight Invoice #</span>
              <input value={invoiceNo} onChange={(e) => setInvoiceNo(e.target.value)}
                     data-testid="recv-freight-invoice-no"
                     className="mt-0.5 w-full rounded border border-slate-300 px-2 py-1 text-sm focus:border-slate-500 focus:outline-none" />
            </label>
            <label className="block">
              <span className="text-xs text-slate-600">Freight Amount ($)</span>
              <input value={amount} onChange={(e) => setAmount(e.target.value)}
                     inputMode="decimal" placeholder="e.g. 660.00"
                     data-testid="recv-freight-amount"
                     className="mt-0.5 w-full rounded border border-slate-300 px-2 py-1 text-sm font-mono focus:border-slate-500 focus:outline-none" />
            </label>
            <label className="block">
              <span className="text-xs text-slate-600">Rate ($/L)</span>
              <input value={perLitre} onChange={(e) => setPerLitre(e.target.value)}
                     inputMode="decimal" placeholder="e.g. 0.0124"
                     data-testid="recv-freight-per-litre"
                     className="mt-0.5 w-full rounded border border-slate-300 px-2 py-1 text-sm font-mono focus:border-slate-500 focus:outline-none" />
            </label>
          </div>
          <label className="block">
            <span className="text-xs text-slate-600">Notes</span>
            <input value={notes} onChange={(e) => setNotes(e.target.value)}
                   data-testid="recv-freight-notes"
                   className="mt-0.5 w-full rounded border border-slate-300 px-2 py-1 text-sm focus:border-slate-500 focus:outline-none" />
          </label>
          <p className="text-[11px] text-slate-500 italic">
            Landed cost = supplier unit cost + freight per litre. Every FIFO layer on this receipt will be finalized on submit.
          </p>
        </div>
        <div className="border-t border-slate-200 px-5 py-3 flex items-center justify-end gap-2">
          <button data-testid="recv-freight-cancel" onClick={onClose} disabled={saving}
                  className="rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60">
            Cancel
          </button>
          <button data-testid="recv-freight-submit" onClick={submit} disabled={saving}
                  className="rounded-md bg-emerald-700 px-4 py-1.5 text-xs font-semibold text-white hover:bg-emerald-800 disabled:opacity-50">
            {saving ? "Attaching…" : "Attach & Finalize"}
          </button>
        </div>
      </div>
    </div>
  );
}


/**
 * (legacy) ThreeWayMatchBlock removed 2026-08-04 · replaced by
 * ReceiptReconciliationBlock per ARC-011. Kept as a compat stub for
 * any residual imports until the next refactor pass.
 */
function ThreeWayMatchBlock({ match }) {
  const overall = match.overall_status || "UNKNOWN";
  const tone = overall === "GREEN"
    ? { border: "border-emerald-200", bg: "bg-emerald-50", text: "text-emerald-900", icon: CheckCircle, pill: "bg-emerald-600" }
    : overall === "AMBER"
      ? { border: "border-amber-200",   bg: "bg-amber-50",   text: "text-amber-900",   icon: Warning,     pill: "bg-amber-600" }
      : overall === "RED"
        ? { border: "border-rose-200",    bg: "bg-rose-50",    text: "text-rose-900",    icon: Warning,     pill: "bg-rose-600" }
        : { border: "border-slate-200",   bg: "bg-slate-50",   text: "text-slate-900",   icon: Info,        pill: "bg-slate-500" };
  const Icon = tone.icon;

  return (
    <section
      className={`rounded-lg border ${tone.border} ${tone.bg} overflow-hidden`}
      data-testid="recv-three-way-match"
    >
      <div className="px-5 py-4 flex items-center gap-3">
        <div className={`rounded-md p-2 text-white ${tone.pill}`}>
          <Icon size={20} weight="fill" />
        </div>
        <div className="flex-1">
          <div className={`text-xs font-semibold uppercase tracking-wider ${tone.text} opacity-80`}>Three-Way Match</div>
          <div className={`text-base font-semibold ${tone.text}`} data-testid="recv-match-headline">
            {overall === "GREEN"   ? "Invoice matches approved delivered cost." :
             overall === "AMBER"   ? "Invoice differs slightly from approved delivered cost." :
             overall === "RED"     ? "Invoice materially differs from approved delivered cost." :
             match.reason || "Match could not be evaluated."}
          </div>
          <p className={`text-xs mt-0.5 ${tone.text} opacity-80`}>
            {match.price_notification_found
              ? `Compared against Price Notification effective ${fmtCalendarDateShort(match.price_notification_effective_date)}`
              : "No matching Price Notification found for this delivery date."}
          </p>
        </div>
      </div>

      {(match.products || []).length > 0 && (
        <table className="w-full text-sm bg-white border-t border-slate-200">
          <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-600 border-b border-slate-200">
            <tr>
              <th className="px-5 py-2">Product</th>
              <th className="px-3 py-2 text-right">Expected Delivered</th>
              <th className="px-3 py-2 text-right">Invoice</th>
              <th className="px-3 py-2 text-right">Contract Discount</th>
              <th className="px-3 py-2 text-right">Variance / L</th>
              <th className="px-3 py-2 text-center">Status</th>
            </tr>
          </thead>
          <tbody>
            {match.products.map((row, i) => {
              const st = row.status;
              const tint = st === "GREEN" ? "bg-emerald-100 text-emerald-800 border-emerald-300"
                         : st === "AMBER" ? "bg-amber-100 text-amber-800 border-amber-300"
                         : st === "RED"   ? "bg-rose-100 text-rose-800 border-rose-300"
                                          : "bg-slate-100 text-slate-700 border-slate-300";
              return (
                <tr key={i} className="border-b border-slate-100 last:border-b-0"
                    data-testid={`recv-match-row-${row.internal_product_code || i}`}>
                  <td className="px-5 py-2 font-semibold text-slate-900">
                    {PRODUCT_LABEL[row.internal_product_code] || row.supplier_description || "—"}
                  </td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums">
                    {row.expected_delivered_cost ? `$${fmtMoney(row.expected_delivered_cost, 4)}` : "—"}
                  </td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums">
                    {row.invoice_unit_price ? `$${fmtMoney(row.invoice_unit_price, 4)}` : "—"}
                  </td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-slate-600">
                    {row.contract_discount ? `-$${fmtMoney(row.contract_discount, 4)}` : "—"}
                  </td>
                  <td className={`px-3 py-2 text-right font-mono tabular-nums ${
                    Number(row.variance_per_litre) > 0.0005 ? "text-rose-800" :
                    Number(row.variance_per_litre) < -0.0005 ? "text-emerald-800" : "text-slate-800"
                  }`}>
                    {row.variance_per_litre != null ? `${Number(row.variance_per_litre) >= 0 ? "+" : ""}$${fmtMoney(row.variance_per_litre, 4)}` : "—"}
                  </td>
                  <td className="px-3 py-2 text-center">
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${tint}`}
                          data-testid={`recv-match-status-${row.internal_product_code || i}`}>
                      {st === "GREEN" ? "Match" : st === "AMBER" ? "Drift" : st === "RED" ? "Mismatch" : "Unknown"}
                    </span>
                    {row.notes?.length > 0 && (
                      <div className="text-[10px] text-slate-500 mt-0.5">{row.notes[0]}</div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}
